import { Pencil } from "lucide-react";
import { useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgIconButton,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { Stack, useTheme } from "@mui/material";

interface RuleDescriptionEditorProps {
  description: string;
  onSave: (description: string) => Promise<void> | void;
  readOnly?: boolean;
  saving?: boolean;
  /**
   * How the editor sits in its host.
   *
   * - `"strip"` (default) — a tinted, bottom-bordered band directly under a
   *   workspace title bar, which is the only place this lived originally.
   * - `"inline"` — bare text in a normal content flow, for the rule detail
   *   page where the description reads as the rule's summary paragraph rather
   *   than as chrome.
   *
   * The band styling was baked into both branches, so a host that wanted the
   * text without the chrome had no way to ask for it.
   */
  chrome?: "strip" | "inline";
}

export const RuleDescriptionEditor = ({
  description,
  onSave,
  readOnly = false,
  saving = false,
  chrome = "strip",
}: RuleDescriptionEditorProps) => {
  const theme = useTheme();
  const [editing, setEditing] = useImmer(false);
  const [draft, setDraft] = useImmer(description);

  useEffect(() => {
    if (!editing) setDraft(description);
  }, [description, editing, setDraft]);

  const trimmedDraft = draft.trim();

  const containerSx =
    chrome === "strip"
      ? {
          bgcolor: theme.igBackgroundColors.baseLightHigh,
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
          px: 2,
          py: 1.5,
        }
      : { py: 0 };

  const handleSave = async () => {
    if (!trimmedDraft || trimmedDraft === description) return;
    await onSave(trimmedDraft);
    setEditing(false);
  };

  if (editing) {
    return (
      <Stack spacing={1} sx={containerSx}>
        <IgTextField
          label="Rule description"
          fullWidth
          multiline
          maxRows={4}
          slotProps={{ htmlInput: { maxLength: 1024 } }}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <IgButton
            size="mini"
            variant="text"
            onClick={() => {
              setDraft(description);
              setEditing(false);
            }}
          >
            Cancel
          </IgButton>
          <IgButton
            size="mini"
            variant="contained"
            loading={saving}
            disabled={!trimmedDraft || trimmedDraft === description}
            onClick={() => void handleSave()}
          >
            Save description
          </IgButton>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{
        ...(chrome === "strip" ? { ...containerSx, py: 1.25 } : containerSx),
        "&:hover .RuleDescriptionEditor-pencil, &:focus-within .RuleDescriptionEditor-pencil":
          {
            opacity: 1,
          },
      }}
    >
      <IgTypography color="text.secondary" variant="body2" sx={{ flex: 1 }}>
        {description}
      </IgTypography>
      {!readOnly ? (
        <IgIconButton
          className="RuleDescriptionEditor-pencil"
          aria-label="Edit rule description"
          size="small"
          onClick={() => setEditing(true)}
          sx={{ opacity: 0, transition: "opacity 120ms ease" }}
        >
          <Pencil size={15} />
        </IgIconButton>
      ) : null}
    </Stack>
  );
};
